import React from "react";
import {ErrorOutlineOutlined, InsertDriveFileOutlined, WarningAmberOutlined} from "@mui/icons-material";
import type {ClContract} from "../contractLinkageData";
import {CL_CURRENCY, daysUntilExpiry} from "../contractLinkageData";
import type {ClContractPatch, ClReviewTx} from "../contractLinkageUtils";
import {
    formatCompactCurrency,
    formatDate,
    formatFullCurrency,
    formatNumber,
    tierColors,
    tierOfConfidence,
} from "../contractLinkageUtils";

type Props = {
    contract: ClContract;
    /** All transactions (decorated) — used for the expiry-leakage alert. */
    rows: ClReviewTx[];
    /** The user's manual corrections for this contract, if any. */
    patch: ClContractPatch | undefined;
};

type Field = {
    label: string;
    /** Key into ClContract, to flag manually edited fields. */
    key: keyof ClContract;
    value: React.ReactNode;
};

const MetaValue: React.FC<{value: React.ReactNode}> = ({value}) =>
    value === null || value === undefined || value === ''
        ? <span className="cl-meta-missing">Not found in document</span>
        : <>{value}</>;

export const ContractOverview: React.FC<Props> = ({contract, rows, patch}) => {
    const c = contract;
    const days = daysUntilExpiry(c);
    const editedKeys = new Set(Object.keys(patch ?? {}));

    // Unlinked spend from this supplier dated after the contract ended — the renewal-gap signal.
    const trailingSpend = React.useMemo(() => {
        if (c.status !== 'expired') return 0;
        return rows
            .filter(tx => tx.supplierId === c.supplierId && !tx.effectiveContractId && tx.date > c.endDate)
            .reduce((s, t) => s + t.amount, 0);
    }, [rows, c]);

    const groups: {title: string; fields: Field[]}[] = [
        {
            title: 'Contract',
            fields: [
                {label: 'Contract type', key: 'type', value: c.type},
                {label: 'Counterparty entity', key: 'counterpartyEntity', value: <MetaValue value={c.counterpartyEntity}/>},
                {label: 'Contract owner', key: 'contractOwner', value: <MetaValue value={c.contractOwner}/>},
                {label: 'Owner department', key: 'ownerDepartment', value: <MetaValue value={c.ownerDepartment}/>},
                {label: 'Signed', key: 'signedDate', value: <MetaValue value={c.signedDate ? formatDate(c.signedDate) : null}/>},
                {label: 'Effective', key: 'startDate', value: formatDate(c.startDate)},
                {
                    label: 'Expires', key: 'endDate', value: <>
                        {formatDate(c.endDate)}
                        {c.status !== 'active' && (
                            <span className={`cl-daysleft is-${c.status}`}>
                                {days < 0 ? `${formatNumber(-days)}d ago` : `in ${formatNumber(days)}d`}
                            </span>
                        )}
                    </>,
                },
            ],
        },
        {
            title: 'Commercial terms',
            fields: [
                {label: 'Contracted value', key: 'contractedValue', value: formatFullCurrency(c.contractedValue, CL_CURRENCY)},
                {label: 'Pricing model', key: 'pricingModel', value: <MetaValue value={c.pricingModel}/>},
                {label: 'Payment terms', key: 'paymentTerms', value: c.paymentTerms},
                {label: 'Price adjustment', key: 'priceAdjustment', value: <MetaValue value={c.priceAdjustment}/>},
            ],
        },
        {
            title: 'Renewal & termination',
            fields: [
                {label: 'Renewal', key: 'renewalType', value: <MetaValue value={c.renewalType}/>},
                {label: 'Renewal notice', key: 'renewalNotice', value: <MetaValue value={c.renewalNotice}/>},
                {label: 'Termination for convenience', key: 'terminationForConvenience', value: <MetaValue value={c.terminationForConvenience}/>},
            ],
        },
        {
            title: 'Legal & service levels',
            fields: [
                {label: 'Governing law', key: 'governingLaw', value: <MetaValue value={c.governingLaw}/>},
                {label: 'Liability cap', key: 'liabilityCap', value: <MetaValue value={c.liabilityCap}/>},
                {label: 'Service levels', key: 'slaSummary', value: <MetaValue value={c.slaSummary}/>},
            ],
        },
    ];

    const confidence = c.extractionConfidence ?? null;
    const confColors = confidence !== null ? tierColors(tierOfConfidence(confidence)) : null;

    return (
        <div className="cl-overview">
            {(c.status !== 'active' || !c.clearTerms) && (
                <div className="cl-overview-alerts">
                    {c.status === 'expired' && (
                        <div className="cl-alert is-danger">
                            <ErrorOutlineOutlined fontSize="small"/>
                            <span>
                                This contract expired on {formatDate(c.endDate)}.
                                {trailingSpend > 0
                                    ? <> <b>{formatCompactCurrency(trailingSpend, CL_CURRENCY)}</b> of unlinked {c.supplier} spend
                                        is dated after the expiry — check the candidates for a renewal gap.</>
                                    : ' No further spend from this supplier was found after the expiry.'}
                            </span>
                        </div>
                    )}
                    {c.status === 'expiring' && (
                        <div className="cl-alert is-warn">
                            <WarningAmberOutlined fontSize="small"/>
                            <span>
                                Expires in <b>{formatNumber(days)} days</b> ({formatDate(c.endDate)}).
                                {c.renewalNotice ? <> Renewal notice: {c.renewalNotice}.</> : ' No renewal notice period was found in the document.'}
                            </span>
                        </div>
                    )}
                    {!c.clearTerms && (
                        <div className="cl-alert is-warn">
                            <WarningAmberOutlined fontSize="small"/>
                            <span>
                                The AI could not fully parse this document
                                {confidence !== null && <> (extraction confidence {confidence}%)</>}.
                                Fields marked “Not found in document” need a manual check — use <b>Edit details</b> to complete them.
                            </span>
                        </div>
                    )}
                </div>
            )}

            <div className="cl-overview-scope">
                <span className="cl-meta-group-title">Scope</span>
                <p>{c.scopeSummary}</p>
            </div>

            <div className="cl-overview-grid">
                {groups.map(group => (
                    <div key={group.title} className="cl-meta-group">
                        <span className="cl-meta-group-title">{group.title}</span>
                        {group.fields.map(f => (
                            <div key={f.label} className="cl-meta-row">
                                <span className="cl-meta-label">
                                    {f.label}
                                    {editedKeys.has(f.key) && <span className="cl-edited-badge">edited</span>}
                                </span>
                                <span className="cl-meta-value">{f.value}</span>
                            </div>
                        ))}
                    </div>
                ))}
            </div>

            <div className="cl-overview-source">
                <InsertDriveFileOutlined fontSize="small"/>
                <span className="cl-overview-source-file"><MetaValue value={c.sourceFile}/></span>
                {c.extractedAt && <span className="cl-overview-source-meta">extracted {formatDate(c.extractedAt)}</span>}
                {confidence !== null && confColors && (
                    <span className="cl-overview-source-conf" style={{backgroundColor: confColors.bg, color: confColors.fg}}>
                        {confidence}% extraction confidence
                    </span>
                )}
                {editedKeys.size > 0 && (
                    <span className="cl-overview-source-meta">
                        · {formatNumber(editedKeys.size)} field{editedKeys.size === 1 ? '' : 's'} manually corrected
                    </span>
                )}
            </div>
        </div>
    );
};
